import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BookOpen, CalendarHeart, Wallet, TrendingUp, CheckCircle2, Megaphone } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, fecha, diasRestantes } from "@/lib/format";
import { Loading, PageTitle, Stat, Empty } from "@/components/Common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const { activeId, activeStudent } = useAuth();
  const [data, setData] = useState(null);
  const [anuncios, setAnuncios] = useState([]);

  useEffect(() => {
    if (!activeId) return;
    setData(null);
    api.get(`/dashboard/${activeId}`).then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/anuncios").then((r) => setAnuncios(r.data));
  }, [activeId]);

  if (!activeId) return <Empty testId="no-student">No hay estudiantes asociados a su cuenta.</Empty>;
  if (data === null) return <Loading />;
  if (data === false) return <Empty>No se pudo cargar el resumen.</Empty>;

  return (
    <div data-testid="parent-dashboard">
      <PageTitle
        title={`Hola, aquí está ${activeStudent?.nombre.split(" ")[0]}`}
        subtitle={`${activeStudent?.grado} · Sección ${activeStudent?.seccion || "—"} · Matrícula ${activeStudent?.matricula}`}>
        <Badge variant={data.contrato_firmado ? "secondary" : "destructive"} data-testid="contract-status-badge">
          {data.contrato_firmado ? "Contrato firmado" : "Contrato pendiente de firma"}
        </Badge>
      </PageTitle>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testId="stat-balance" label="Balance pendiente" value={money(data.balance_pendiente)}
              tone={data.balance_pendiente > 0 ? "warn" : "good"} icon={Wallet}
              hint={data.facturas_pendientes ? `${data.facturas_pendientes} factura(s) por pagar` : "Cuenta al día"} />
        <Stat testId="stat-promedio" label="Promedio general" value={data.promedio || "—"} tone="good"
              icon={TrendingUp} hint="Acumulado del año lectivo" />
        <Stat testId="stat-tareas" label="Tareas pendientes" value={data.tareas_pendientes} icon={BookOpen}
              tone={data.tareas_pendientes > 0 ? "warn" : "good"} hint="Por entregar o confirmar" />
        <Stat testId="stat-asistencia" label="Asistencia" value={`${data.asistencia_pct}%`} icon={CheckCircle2}
              hint="Últimos registros del periodo" />
      </div>

      <div className="mt-8 grid gap-8 md:grid-cols-12">
        <section className="md:col-span-7 space-y-6">
          <div className="rounded-lg border border-border/60 bg-card p-6 sm:p-8">
            <div className="flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-accent" />
              <h2 className="font-display text-xl font-bold tracking-tight">Novedades del centro</h2>
            </div>
            <div className="mt-6 space-y-5" data-testid="feed-anuncios">
              {anuncios.map((a, i) => (
                <article key={a.id} className="rise border-l-2 border-secondary pl-5"
                         style={{ animationDelay: `${i * 70}ms` }}>
                  <div className="flex items-center gap-3">
                    <h3 className="font-display text-base font-semibold">{a.titulo}</h3>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-widest">{a.categoria}</Badge>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.cuerpo}</p>
                  <p className="mt-2 text-xs text-muted-foreground/70">{fecha(a.creado_en)} · {a.autor}</p>
                </article>
              ))}
              {anuncios.length === 0 && <p className="text-sm text-muted-foreground">Sin anuncios por ahora.</p>}
            </div>
          </div>
        </section>

        <aside className="md:col-span-5 space-y-6">
          {data.proxima_factura && (
            <div className="relative overflow-hidden rounded-lg border border-accent/40 bg-card p-6 sm:p-8"
                 data-testid="proximo-vencimiento">
              <span className="absolute left-0 top-0 h-full w-1 bg-accent" />
              <div className="flex items-center gap-2 text-accent">
                <AlertTriangle className="h-4 w-4" />
                <p className="text-xs font-bold uppercase tracking-[0.2em]">Próximo vencimiento</p>
              </div>
              <p className="mt-4 font-display text-3xl font-extrabold tracking-tight">
                {money(data.proxima_factura.monto_total)}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {data.proxima_factura.detalle?.[0]?.concepto} · vence {fecha(data.proxima_factura.fecha_vencimiento)}
                {diasRestantes(data.proxima_factura.fecha_vencimiento) < 0 ? " (vencida)" : ""}
              </p>
              <Button asChild className="mt-6 w-full rounded-full" data-testid="ir-a-pagar">
                <Link to="/portal/finanzas">Pagar ahora</Link>
              </Button>
            </div>
          )}

          <div className="rounded-lg border border-border/60 bg-card p-6 sm:p-8">
            <h2 className="font-display text-xl font-bold tracking-tight">Tareas por entregar</h2>
            <div className="mt-5 space-y-4" data-testid="proximas-tareas">
              {data.proximas_tareas.map((t) => (
                <div key={t.id} className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">{t.titulo}</p>
                    <p className="text-xs text-muted-foreground">{t.materia_nombre}</p>
                  </div>
                  <span className="shrink-0 font-mono text-xs text-accent">{fecha(t.fecha_entrega)}</span>
                </div>
              ))}
              {data.proximas_tareas.length === 0 && (
                <p className="text-sm text-muted-foreground">Todo entregado. ¡Excelente!</p>
              )}
            </div>
            <Button asChild variant="outline" className="mt-6 w-full rounded-full">
              <Link to="/portal/academico" data-testid="ver-academico">Ver módulo académico</Link>
            </Button>
          </div>

          <div className="rounded-lg border border-border/60 bg-card p-6 sm:p-8">
            <div className="flex items-center gap-2">
              <CalendarHeart className="h-4 w-4 text-primary" />
              <h2 className="font-display text-xl font-bold tracking-tight">Próximas actividades</h2>
            </div>
            <div className="mt-5 space-y-4" data-testid="proximas-actividades">
              {data.proximas_actividades.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-4">
                  <p className="text-sm font-medium">{a.nombre}</p>
                  <span className="font-mono text-xs text-muted-foreground">{fecha(a.fecha_evento)}</span>
                </div>
              ))}
              {data.proximas_actividades.length === 0 && (
                <p className="text-sm text-muted-foreground">Sin actividades próximas.</p>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
